import type { CSSProperties } from 'react'
import { convertMMDDYYYYToYYYYMMDD } from '../../../utils/date'
import {
  getTimeOffTypeLabel,
  TimeOffDuration,
} from '../../../utils/timeOffTypes'
import type { TooltipData, CalendarRow, Cell } from './Datepicker.types'

export const pad2 = (n: number): string => String(n).padStart(2, '0')

export const formatMMDDYYYY = (yy: number, mm: number, dd: number): string =>
  `${pad2(mm + 1)}/${pad2(dd)}/${yy}`

export const parseValue = (
  v: string | string[] | undefined,
  today: Date
): { month: number; year: number } => {
  if (!v) return { month: today.getMonth(), year: today.getFullYear() }
  const str = Array.isArray(v) ? v[0] : v
  if (typeof str !== 'string')
    return { month: today.getMonth(), year: today.getFullYear() }
  const parts = str.split('/').map((p) => Number.parseInt(p, 10))
  if (
    parts.length === 3 &&
    !Number.isNaN(parts[0]) &&
    !Number.isNaN(parts[1]) &&
    !Number.isNaN(parts[2])
  ) {
    const yearPart = parts[2]
    const validYear =
      yearPart >= 1000 && yearPart <= 9999 ? yearPart : today.getFullYear()
    return { month: parts[0] - 1, year: validYear }
  }
  return { month: today.getMonth(), year: today.getFullYear() }
}

/**
 * Returns true when dayVal (MM/DD/YYYY) is strictly after a given threshold
 * date (also MM/DD/YYYY).  Used by the Start Date datepicker in range pickers
 * to prevent the user from selecting a date later than the already-chosen End Date.
 *
 * Returns false when threshold is empty or invalid so the restriction is a
 * no-op when no end date has been chosen yet.
 */
export const isAfterDate = (dayVal: string, thresholdMMDDYYYY: string): boolean => {
  if (!thresholdMMDDYYYY) return false
  const [m, d, y] = dayVal.split('/').map(Number)
  const [tm, td, ty] = thresholdMMDDYYYY.split('/').map(Number)
  if ([m, d, y, tm, td, ty].some(Number.isNaN)) return false
  return new Date(y, m - 1, d) > new Date(ty, tm - 1, td)
}

/**
 * Returns true when dayVal (MM/DD/YYYY) is strictly before a given threshold
 * date (also MM/DD/YYYY).  Used by the End Date datepicker in range pickers
 * to prevent the user from selecting a date earlier than the Start Date.
 *
 * Returns false when threshold is empty or invalid so the restriction is a
 * no-op when no start date has been chosen yet.
 */
export const isBeforeDate = (dayVal: string, thresholdMMDDYYYY: string): boolean => {
  if (!thresholdMMDDYYYY) return false
  const [m, d, y] = dayVal.split('/').map(Number)
  const [tm, td, ty] = thresholdMMDDYYYY.split('/').map(Number)
  if ([m, d, y, tm, td, ty].some(Number.isNaN)) return false
  return new Date(y, m - 1, d) < new Date(ty, tm - 1, td)
}

/**
 * Returns true when dayVal (MM/DD/YYYY) refers to today or a date strictly after today.
 * Used by Datepicker when disableFutureDates is enabled so the user can only
 * select past dates (history date-range pickers).
 */
export const isFutureDate = (dayVal: string, today: Date): boolean => {
  const [m, d, y] = dayVal.split('/').map(Number)
  const dateToCheck = new Date(y, m - 1, d)
  const todayDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  )
  return dateToCheck >= todayDate
}

export const isPastDate = (dayVal: string, today: Date): boolean => {
  const [m, d, y] = dayVal.split('/').map(Number)
  const dateToCheck = new Date(y, m - 1, d)
  const todayDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  )
  return dateToCheck < todayDate
}

export const buildCells = (year: number, month: number): Cell[] => {
  const firstDay = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const prevMonthLastDay = new Date(year, month, 0).getDate()
  const cells: Cell[] = []
  for (let i = 0; i < firstDay; i++) {
    cells.push({ day: prevMonthLastDay - (firstDay - 1) + i, inMonth: false })
  }
  for (let d = 1; d <= daysInMonth; d++) cells.push({ day: d, inMonth: true })
  return cells
}

export const transformTableRowsToTooltipData = (
  tableRows: CalendarRow[]
): Record<string, TooltipData> => {
  if (!tableRows || tableRows.length === 0) return {}

  const groupedByDate: Record<string, CalendarRow[]> = {}

  tableRows.forEach((row) => {
    if (!groupedByDate[row.date]) {
      groupedByDate[row.date] = []
    }
    groupedByDate[row.date].push(row)
  })

  const tooltipDataMap: Record<string, TooltipData> = {}

  Object.entries(groupedByDate).forEach(([date, rows]) => {
    const dateKey = convertMMDDYYYYToYYYYMMDD(date)
    // Count unique requests, not allocations/rows
    const uniqueRequests = Array.from(
      new Map(rows.map((row) => [row.toReqId, row])).values()
    )

    const totalHours = rows.reduce(
      (sum, r) => sum + Number(r.requestedHours || 0),
      0
    )

    const firstRow = rows[0]
    const durationFlag = firstRow?.durationFlag || 'F'
    const durationLabel = TimeOffDuration[durationFlag] || 'Full Day'

    const entry: TooltipData = {
      durationLabel,
      date: dateKey,

      allocations: rows.map((r) => ({
        typeLabel: getTimeOffTypeLabel(r.requestType),
        hours: Number(r.requestedHours || 0),
      })),

      totalHours,

      // Show "See more" only when there are multiple actual requests
      hasMultipleRequests: uniqueRequests.length > 1,

      // Convert requests into TooltipData expected structure
      individualRequests: uniqueRequests.map((request) => {
        const requestRows = rows.filter(
          (row) => row.toReqId === request.toReqId
        )

        return {
          startTime: request.startTime,
          endTime: request.endTime,

          allocations: requestRows.map((row) => ({
            typeLabel: getTimeOffTypeLabel(row.requestType),
            hours: Number(row.requestedHours || 0),
          })),

          totalHours: requestRows.reduce(
            (sum, row) => sum + Number(row.requestedHours || 0),
            0
          ),
        }
      }),
   // Add this
  otherRequests: uniqueRequests.map((request) => ({
    toReqId: request.toReqId ?? '',
    status: request.status ?? '',
  })),


    }

    if (firstRow?.startTime) {
      entry.startTime = firstRow.startTime
    }

    if (firstRow?.endTime) {
      entry.endTime = firstRow.endTime
    }

    tooltipDataMap[dateKey] = entry
  })

  return tooltipDataMap
}

/**
 * Returns the button style for dates that belong to another user's request.
 * Extracted from getCalendarViewButtonStyle to reduce cognitive complexity.
 */
const getOtherRequestButtonStyle = (
  isPartialDayRequest: boolean,
  isOnHold: boolean,
  isVacationBidRequest: boolean,
  isTodayDate: boolean
): { buttonClasses: string; buttonStyles: CSSProperties; hasBottomDot: boolean } => {
  const hasBottomDot = isTodayDate
  if (isPartialDayRequest) {
    return {
      buttonClasses: 'cursor-not-allowed',
      buttonStyles: {
        background: 'linear-gradient(to right, #A4D751 50%, #ffffff 50%)',
        border: '2px solid #A4D751',
        color: '#1f2937',
        cursor: 'not-allowed',
      },  
      hasBottomDot,
    }
  }
  if (isOnHold) {
    return {
      buttonClasses: 'cursor-pointer hover:opacity-80',
      buttonStyles: { backgroundColor: '#F36C0D', color: '#ffffff', cursor: 'pointer' },
      hasBottomDot,
    }
  }
  if (isVacationBidRequest) {
    return {
      buttonClasses: 'cursor-not-allowed',
      buttonStyles: {
        backgroundColor: '#F36C0D',
        color: '#ffffff',
        cursor: 'not-allowed',
      },
      hasBottomDot,
    }
  }
  return {
    buttonClasses: 'cursor-not-allowed',
    buttonStyles: { backgroundColor: '#A4D751', color: '#1f2937', cursor: 'not-allowed' },
    hasBottomDot,
  }
}

export interface CalendarViewButtonStyleParams {
  isDisabled: boolean
  isOtherRequest: boolean
  isPartialDayRequest: boolean
  isOnHold: boolean
  isVacationBidRequest: boolean
  isSelected: boolean
  isOtherSelected: boolean
  isCommittedSelected: boolean
  isHighlighted: boolean
  isTodayDate: boolean
}

export const getCalendarViewButtonStyle = ({
  isDisabled,
  isOtherRequest,
  isPartialDayRequest,
  isOnHold,
  isVacationBidRequest,
  isSelected,
  isOtherSelected,
  isCommittedSelected,
  isHighlighted,
  isTodayDate,
}: CalendarViewButtonStyleParams): {
  buttonClasses: string
  buttonStyles: CSSProperties
  hasBottomDot: boolean
} => {
  let buttonClasses = 'hover:bg-gray-100'
  let buttonStyles: CSSProperties = {}
  let hasBottomDot = false

  if (isDisabled && !isOtherRequest && !isHighlighted) {
    return { buttonClasses: 'cursor-not-allowed', buttonStyles: { color: '#d1d5db', cursor: 'not-allowed' }, hasBottomDot }
  }
  if (isOtherRequest) {
    return getOtherRequestButtonStyle(
      isPartialDayRequest,
      isOnHold,
      isVacationBidRequest,
      isTodayDate
    )
  }
  if (isSelected) {
    buttonClasses = 'bg-white text-white font-semibold'
    buttonStyles = { backgroundColor: '#0078B3', color: '#ffffff' }
  } else if (isCommittedSelected) {
    // Previously reviewed dates in post-review add mode: light blue + clickable (no cursor-not-allowed)
    buttonClasses = 'bg-white font-semibold'
    buttonStyles = { backgroundColor: '#D1E7F0', color: '#1f2937' }
  } else if (isOtherSelected) {
    buttonClasses = 'cursor-not-allowed'
    buttonStyles = { backgroundColor: '#D1E7F0', color: '#1f2937', cursor: 'not-allowed' }
  } else if (isHighlighted) {
    buttonClasses = 'font-semibold'
    buttonStyles = { color: '#1f2937' }
  } else if (isTodayDate) {
    buttonClasses = 'font-semibold'
    buttonStyles = { color: '#0078B3' }
    hasBottomDot = true
  }

  return { buttonClasses, buttonStyles, hasBottomDot }
}

export const getCursorClass = (
  isDisabled: boolean,
  readOnly: boolean,
  buttonClasses: string
): string => {
  if (isDisabled) return 'cursor-not-allowed'
  if (readOnly) return 'cursor-default'
  return `cursor-pointer ${buttonClasses}`
}

export const getIconViewButtonStyle = (
  isSelected: boolean,
  isDisabled: boolean = false,
  isTodayDate: boolean = false
): { buttonClasses: string; buttonStyles: CSSProperties } => {
  // Disabled dates (past dates)
  if (isDisabled) {
    return {
      buttonClasses: 'cursor-not-allowed',
      buttonStyles: { color: '#d1d5db', cursor: 'not-allowed' }
    }
  }
  
  // Selected date
  if (isSelected) {
    return {
      buttonClasses: 'bg-white text-white font-semibold',
      buttonStyles: { backgroundColor: '#0078B3', color: '#ffffff' }
    }
  }
  
  // Today's date (not selected)
  if (isTodayDate) {
    return {
      buttonClasses: 'font-semibold hover:bg-gray-100',
      buttonStyles: { color: '#0078B3' }
    }
  }
  
  // Regular date
  return {
    buttonClasses: 'hover:bg-gray-100',
    buttonStyles: {}
  }
}
